(() => {
  if (window.minimalistartHeroInitialized) return;
  window.minimalistartHeroInitialized = true;
  const storageKey = 'minimalistart-lighting';
  const eventName = 'minimalistart:lighting-change';
  const instances = new Map();
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const staticFadeStops = [[0, 0], [.22, .16], [.62, .65], [1, 1]];
  const scrollFadeStops = [[0, 0], [.2, .18], [.6, .72], [.92, 1], [1, 1]];
  let scrollScheduled = false;
  let currentLighting = root.dataset.lighting === 'on' ? 'on' : 'off';

  try {
    const savedLighting = localStorage.getItem(storageKey);
    if (savedLighting === 'on' || savedLighting === 'off') currentLighting = savedLighting;
  } catch {}

  root.dataset.lighting = currentLighting;

  const clamp = (value) => Math.max(0, Math.min(1, value));

  const gradientOpacity = (position, stops) => {
    const point = clamp(position);
    for (let index = 1; index < stops.length; index += 1) {
      const [endPosition, endOpacity] = stops[index];
      if (point > endPosition) continue;
      const [startPosition, startOpacity] = stops[index - 1];
      return startOpacity + ((point - startPosition) / (endPosition - startPosition)) * (endOpacity - startOpacity);
    }
    return 1;
  };

  const prepareImage = async (image, signal) => {
    if (!image) throw new Error('Missing scene image');
    if (!image.complete) {
      await new Promise((resolve, reject) => {
        const cleanUp = () => {
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', failed);
          signal.removeEventListener('abort', failed);
        };
        const loaded = () => {
          cleanUp();
          resolve();
        };
        const failed = () => {
          cleanUp();
          reject(new Error('Scene image unavailable'));
        };
        image.addEventListener('load', loaded, { once: true });
        image.addEventListener('error', failed, { once: true });
        signal.addEventListener('abort', failed, { once: true });
        if (signal.aborted) failed();
        else if (image.complete) image.naturalWidth > 0 ? loaded() : failed();
      });
    }
    if (!image.naturalWidth || signal.aborted) throw new Error('Scene image unavailable');
    if (typeof image.decode === 'function') {
      try {
        await image.decode();
      } catch {
        if (!image.naturalWidth || signal.aborted) throw new Error('Scene image unavailable');
      }
    }
  };

  const applyLighting = (lighting, persist = false) => {
    currentLighting = lighting === 'on' ? 'on' : 'off';
    root.dataset.lighting = currentLighting;

    instances.forEach(({ hero, button, label, day, night, staticScene }) => {
      const isOn = currentLighting === 'on' && (staticScene || hero.dataset.lightingEnabled === 'true');
      hero.dataset.lighting = isOn ? 'on' : 'off';
      day?.setAttribute('aria-hidden', String(!staticScene && isOn));
      night?.setAttribute('aria-hidden', String(staticScene || !isOn));
      if (!button) return;
      button.setAttribute('aria-pressed', String(isOn));
      if (label) label.textContent = isOn ? button.dataset.labelOn : button.dataset.labelOff;
    });

    if (persist) {
      try {
        localStorage.setItem(storageKey, currentLighting);
      } catch {}
    }
  };

  const updateFade = () => {
    scrollScheduled = false;
    instances.forEach(({ hero, header, fadeDepth }) => {
      const bounds = hero.getBoundingClientRect();
      if (hero.hasAttribute('data-header-only')) {
        header?.style.setProperty('--ma-logo-scale', (1 - clamp(window.scrollY / 160) * .3).toFixed(4));
        header?.style.setProperty('--ma-header-backdrop', '1');
        hero.setAttribute('data-header-solid', '');
        return;
      }
      const progress = clamp(-bounds.top / (bounds.height * .75));
      hero.style.setProperty('--ma-scroll-fade', progress.toFixed(4));
      const shrinkProgress = clamp(-bounds.top / 160);
      header?.style.setProperty('--ma-logo-scale', (1 - shrinkProgress * .3).toFixed(4));
      const headerHeight = header?.offsetHeight || 0;
      const samplePosition = (-bounds.top + headerHeight * .7) / bounds.height;
      const staticOpacity = gradientOpacity((samplePosition - (1 - fadeDepth)) / fadeDepth, staticFadeStops);
      const scrollOpacity = reducedMotion.matches ? 0 : gradientOpacity((samplePosition - .15) / .85, scrollFadeStops) * progress;
      const fadeOpacity = 1 - (1 - staticOpacity) * (1 - scrollOpacity);
      const fadeContrastProgress = clamp((fadeOpacity - .15) / .25);
      const exitProgress = clamp((headerHeight + 90 - bounds.bottom) / 90);
      const solidProgress = Math.max(exitProgress, fadeContrastProgress);
      header?.style.setProperty('--ma-header-backdrop', solidProgress.toFixed(4));
      hero.toggleAttribute('data-header-solid', solidProgress > .08);
    });
  };

  const scheduleFade = () => {
    if (scrollScheduled) return;
    scrollScheduled = true;
    requestAnimationFrame(updateFade);
  };

  const initializeHero = (hero) => {
    if (instances.has(hero)) return;
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const button = hero.querySelector('[data-ma-lights]');
    const drawer = hero.querySelector('[data-ma-drawer]');
    const panel = drawer?.querySelector('[data-ma-panel]');
    const summary = drawer?.querySelector(':scope > summary');
    const closeButton = drawer?.querySelector('[data-ma-close]');
    const instance = {
      hero,
      header: hero.querySelector('.ma-header'),
      button,
      label: hero.querySelector('[data-ma-light-label]'),
      day: hero.querySelector('[data-ma-day]'),
      night: hero.querySelector('[data-ma-night]'),
      staticScene: hero.hasAttribute('data-static-scene'),
      fadeDepth: Math.max(.01, parseFloat(hero.style.getPropertyValue('--ma-fade-depth')) / 100 || .3),
      abortController,
      drawer,
      releaseDrawer: () => {}
    };
    instances.set(hero, instance);
    applyLighting(currentLighting);

    if (button && (instance.staticScene || hero.dataset.lightingEnabled === 'true')) {
      button.hidden = false;
      button.addEventListener('click', async () => {
        const lighting = currentLighting === 'on' ? 'off' : 'on';
        const scene = instance.staticScene || lighting === 'off' ? instance.day : instance.night;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        try {
          if (!hero.hasAttribute('data-header-only')) await prepareImage(scene?.querySelector('img'), abortController.signal);
          if (abortController.signal.aborted || !hero.isConnected) return;
          applyLighting(lighting, true);
          document.dispatchEvent(new CustomEvent(eventName, { detail: { lighting } }));
        } catch {} finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }, options);
    }

    hero.querySelectorAll('[data-ma-localization]').forEach((form) => {
      form.querySelector('select')?.addEventListener('change', () => {
        const returnTo = form.querySelector('input[name="return_to"]');
        if (returnTo) returnTo.value = window.location.pathname + window.location.search;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      }, options);
    });

    if (drawer && panel && summary) {
      let previousOverflow = '';
      let drawerIsActive = false;
      let previouslyFocused = null;

      const releaseDrawer = () => {
        if (!drawerIsActive) return;
        drawerIsActive = false;
        document.body.style.overflow = previousOverflow;
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        panel.removeAttribute('aria-label');
      };

      const closeDrawer = (restoreFocus = true) => {
        drawer.open = false;
        summary.setAttribute('aria-expanded', 'false');
        releaseDrawer();
        if (restoreFocus) {
          const target = previouslyFocused?.isConnected ? previouslyFocused : summary;
          target.focus({ preventScroll: true });
        }
      };

      const activateDrawer = () => {
        if (drawerIsActive) return;
        drawerIsActive = true;
        previouslyFocused = document.activeElement;
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        panel.setAttribute('aria-label', summary.getAttribute('aria-label'));
        summary.setAttribute('aria-expanded', 'true');
        (closeButton || panel).focus({ preventScroll: true });
      };

      instance.releaseDrawer = releaseDrawer;
      drawer.dataset.maEnhanced = '';
      summary.setAttribute('aria-expanded', String(drawer.open));
      if (closeButton) closeButton.hidden = false;

      drawer.addEventListener('toggle', () => {
        if (drawer.open) activateDrawer();
        else {
          summary.setAttribute('aria-expanded', 'false');
          releaseDrawer();
        }
      }, options);
      closeButton?.addEventListener('click', () => closeDrawer(), options);
      drawer.querySelector('[data-ma-dismiss]')?.addEventListener('click', () => closeDrawer(), options);

      document.addEventListener('keydown', (event) => {
        if (!drawer.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), select, summary, [tabindex="0"]')]
          .filter((element) => !element.hidden && element.getClientRects().length > 0);
        if (!focusable.length) {
          event.preventDefault();
          panel.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      }, options);

      document.addEventListener('focusin', (event) => {
        if (drawer.open && drawerIsActive && !panel.contains(event.target)) {
          (closeButton || panel).focus({ preventScroll: true });
        }
      }, options);

      panel.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) closeDrawer(false);
      }, options);

      if (drawer.open) activateDrawer();
    }

    hero.dataset.maReady = '';
    updateFade();
  };

  const initialize = (scope = document) => {
    if (scope.matches?.('[data-ma-hero]')) initializeHero(scope);
    scope.querySelectorAll('[data-ma-hero]').forEach(initializeHero);
  };

  document.addEventListener(eventName, (event) => {
    const lighting = event.detail?.lighting;
    if (lighting === 'on' || lighting === 'off') applyLighting(lighting, true);
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey || !['on', 'off'].includes(event.newValue)) return;
    applyLighting(event.newValue);
    document.dispatchEvent(new CustomEvent(eventName, { detail: { lighting: event.newValue } }));
  });
  window.addEventListener('scroll', scheduleFade, { passive: true });
  window.addEventListener('resize', scheduleFade, { passive: true });
  window.addEventListener('pageshow', updateFade);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, hero) => {
      if (event.target.contains(hero)) {
        instance.releaseDrawer();
        instance.abortController.abort();
        instances.delete(hero);
      }
    });
  });

  initialize();
})();
